'use client';

/**
 * callEndedStore: the ONE client-side answer to "has this call ended?".
 *
 * Two things can tell the page a call is over: LiveKit's own disconnect reason (ROOM_DELETED / CLIENT_INITIATED, the
 * officer's own End) and the server's verdict (/api/room-status/prejoin, read by useCallEndedAuthGate). A page that
 * was offline when the call ended never hears a reason, so the server verdict is the only signal it gets. Every
 * source writes here (useCallEndedAuthGate is the only writer); every surface reads here (the shared connection
 * banner and indicator, the call clock, the call page's own "Call ended" banner), so they cannot disagree.
 *
 * Keyed by room name (the call id). Module-level, like lib/connection-status: one store per browser tab.
 * The first ended source for a room wins until a live connection (or the page leaving) clears the mark, so an ended
 * verdict can never outlive a rejoined call. One exception: the server's verdict is the stronger fact, so it upgrades
 * a 'signal' mark. A real network cut can deliver ROOM_DELETED to a page that is offline; without the upgrade the server's
 * answer, read once the network returns, was thrown away and the page kept offering a Re-join that can only answer 410.
 */

import { useSyncExternalStore } from 'react';

/**
 * Where an ended mark came from. 'server' = the server's own verdict, read while this page was not connected (it never
 * heard a reason): the page has nothing live left to finish. 'signal' = an ended disconnect reason or the officer's own
 * End on a page that is still there to finish the job (stop recording, offer Re-join): the call page keeps its own UI.
 */
export type CallEndedSource = 'server' | 'signal';

const ended = new Map<string, CallEndedSource>();
/** Rooms whose post-online ended verdict is being read right now (count per room: each gate holds at most one). */
const verdictPending = new Map<string, number>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

/** The call in this room is over (server verdict, ended disconnect reason, or the officer's own End). */
export function markRoomEnded(roomName: string | null | undefined, source: CallEndedSource = 'signal'): void {
  if (!roomName) return;
  // Keep the first source until the room is marked live, except that the server's verdict outranks a signal.
  const current = ended.get(roomName);
  if (current === source || (current === 'server' && source === 'signal')) return;
  ended.set(roomName, source);
  emit();
}

/** The room is connected again (rejoin) or the page that owned the verdict is gone: not ended any more. */
export function markRoomLive(roomName: string | null | undefined): void {
  if (!roomName || !ended.delete(roomName)) return;
  emit();
}

export function isRoomEnded(roomName: string | null | undefined): boolean {
  return Boolean(roomName) && ended.has(roomName as string);
}

/** The ended verdict came from the server while the page was not connected (see CallEndedSource). */
export function isRoomEndedByServer(roomName: string | null | undefined): boolean {
  return Boolean(roomName) && ended.get(roomName as string) === 'server';
}

/**
 * The network just came back and this room's ended verdict is being read (useCallEndedAuthGate, the first read after
 * `online`, bounded by its short deadline). Until it settles the page does not know whether the call is live, so the
 * connection banner must not claim "Reconnecting to the call…" for it (#2030 rule 1). Call the returned release once.
 */
export function markRoomVerdictPending(roomName: string | null | undefined): () => void {
  if (!roomName) return () => {};
  verdictPending.set(roomName, (verdictPending.get(roomName) ?? 0) + 1);
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const left = (verdictPending.get(roomName) ?? 1) - 1;
    if (left > 0) verdictPending.set(roomName, left); else verdictPending.delete(roomName);
    emit();
  };
}

export function isRoomVerdictPending(roomName: string | null | undefined): boolean {
  return Boolean(roomName) && verdictPending.has(roomName as string);
}

export function subscribeCallEnded(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** True once the call in this room is known to have ended. */
export function useCallEnded(roomName: string | null | undefined): boolean {
  return useSyncExternalStore(
    subscribeCallEnded,
    () => isRoomEnded(roomName),
    () => false,
  );
}

/** True once the server has said the call ended while this page was not connected: the page lands on CallEndedNotice. */
export function useServerSaysCallEnded(roomName: string | null | undefined): boolean {
  return useSyncExternalStore(
    subscribeCallEnded,
    () => isRoomEndedByServer(roomName),
    () => false,
  );
}
